import React, { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import { Box } from '@firsttable/web-components/atoms';
import { currencyFormat } from '@firsttable/functions';
import BodyTitle from '@firsttable/web-components/atoms/BodyTitle';
import Text from '@firsttable/web-components/atoms/Text';
import Head from 'next/head';
import { PromoCode } from '@firsttable/types';
import ErrorBoundary from '@components/organisms/ErrorBoundary/ErrorBoundary';
import useBackUrl from '@/components/hooks/backUrl';
import { InitialStateType } from '@/components/context/AuthContext/reducer';
import { pushToDataLayer } from '@/lib/analytics/gtm/dataLayer';
import { useGtmEnabled } from '@/lib/analytics/gtm/useGtmEnabled';
import SignUp from '../../components/organisms/Auth/SignUp';
import { initializeApollo } from '../../lib/apollo';
import { GlobalState } from '../../components/context/Silverstripe';
import SiteLayout from '../../components/layouts/SiteLayout';
import SEO from '../../components/SEO';

const LoadingPromoItem = () => (
  <Box display={['block', 'flex']} maxWidth="860px" mb="30px">
    <Box flex={1} className="pulse-animation">
      <Box bg="grayscale.300" width={['60%', '41%']} height="19px" mb="38px" mt="10px" />
      <Box bg="grayscale.300" width="75%" height="12px" />
    </Box>
  </Box>
);

const GET_PROMO = gql`
  query getPromo($code: String!, $siteId: Int!) {
    PromoCode(code: $code, siteId: $siteId) {
      id
      code
      name
      freeCreditNice
      freeCredit
      freeCreditCurrency
    }
  }
`;

const siteId = +process.env.NEXT_PUBLIC_SITE_ID;

const InviteLayout = ({ promoCode }: { promoCode: PromoCode }) => {
  const router = useRouter();

  // Can we set a back url like /redirect/page/6 to get us to QT?
  useBackUrl(useCallback((state: InitialStateType) => state?.user?.city?.slug, []));

  const promoCodeValue = promoCode?.code;
  const gtmEnabled = useGtmEnabled();

  // Push the promo UTM attribution in an effect (not during render) so it fires once per promo code instead of on every render, and only once GTM is consent-enabled. Keyed on gtmEnabled so a later Accept still attributes — a render-time push can be dropped before enableGtm runs.
  useEffect(() => {
    if (!gtmEnabled || !promoCodeValue) {
      return;
    }
    pushToDataLayer({
      utm_campaign: promoCodeValue,
      utm_medium: 'PromoCode',
      utm_source: 'PromoCode',
    });
  }, [gtmEnabled, promoCodeValue]);

  if (router.isFallback) {
    return (
      <SignUp isModal={false}>
        <LoadingPromoItem />
      </SignUp>
    );
  }

  const { id, code, freeCredit, freeCreditCurrency, name } = promoCode;

  let title = '';

  if (code) {
    title = `Sign up now`;
    if (freeCredit > 0) {
      title += ` to claim ${currencyFormat(freeCredit || 0, freeCreditCurrency || '')} free credit`;
    } else {
      title += ` with promo code ${code}`;
    }
    if (name) {
      title += ` from ${name}!`;
    }
  }

  return (
    <ErrorBoundary>
      <SignUp isModal={false} inviteCode={code}>
        <SEO
          title={title}
          slug={`/invite/${code}`}
          ogImage={`https://mop.firsttable.com/api/image/promocode/og/${id}/${siteId}`}
        />
        <BodyTitle as="h3">{title}</BodyTitle>
        <Text>
          First Table is a website that allows you to get 50% off the first table of the night at
          participating restaurants.
        </Text>
      </SignUp>
    </ErrorBoundary>
  );
};

const InvitePage = ({
  globalData,
  promoCode,
}: {
  globalData: GlobalState;
  promoCode: PromoCode;
}) => (
  <SiteLayout appState={{ global: { ...globalData } }} showMenu={false} showFooter={false}>
    <Head>
      {globalData && (
        <link
          rel="canonical"
          href={`https://${globalData.siteConfig.websiteURL}/first-table-promo-code`}
        />
      )}
    </Head>
    <InviteLayout promoCode={promoCode} />
  </SiteLayout>
);

export default InvitePage;

export async function getStaticProps({ params: { slug }, preview, revalidateReason }) {
  const byPassCache = revalidateReason === 'on-demand';
  const apolloClient = initializeApollo(null, preview, byPassCache);
  const [{ data: promoData }] = await Promise.all([
    apolloClient.query({
      query: GET_PROMO,
      variables: {
        code: slug[0],
        siteId,
      },
    }),
  ]);

  if (!promoData.PromoCode) {
    return {
      notFound: true,
    };
  }

  return {
    props: { promoCode: promoData.PromoCode },
  };
}

export async function getStaticPaths() {
  const paths = [];
  return {
    paths,
    fallback: true, // Don't await ssr and show a loading page. Bots will await the ssr page.
  };
}
